// The display language of the Profit Yield surface, in one place.
//
// Ported from Meteoric's `src/lib/profit-yield-view.ts` with the numbers
// unchanged - the same figure must read identically in both places while the
// two surfaces overlap, or a reader comparing them concludes one is wrong.

import type { Tone } from "@/lib/tone";

import type { Grain } from "@/lib/meteoric-regions";

import type { Maturity, SeriesPoint } from "./api";

export {
  bandTone,
  DATA_FRESH_MAX_DAYS,
  DATA_WARN_MAX_DAYS,
  daysSince,
  freshnessState,
  GRAINS,
  isGrain,
  regionFromDeepLink,
  regionLabel,
  regionsFrom,
} from "@/lib/meteoric-regions";
export type { FreshnessState, Grain } from "@/lib/meteoric-regions";

/**
 * A GBP amount, never a bare number.
 *
 * Under GBP 1,000 keeps 2dp because the difference between 12.40 and 12.44 is
 * the whole point at that size; at or above it, pennies are noise and the
 * thousands separator is what makes the figure scannable.
 */
export function formatGbp(value: number | null): string {
  if (value === null) return "n/a";
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: abs >= 1000 ? 0 : 2,
    maximumFractionDigits: abs >= 1000 ? 0 : 2,
  }).format(abs);
  return value < 0 ? `-${formatted}` : formatted;
}

/** The headline ratio. Always 2dp - these numbers live between about 0 and 5. */
export function formatRatio(value: number | null): string {
  if (value === null) return "n/a";
  return value.toFixed(2);
}

export function formatCount(value: number | null): string {
  if (value === null) return "n/a";
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value);
}

export function formatPct(value: number | null): string {
  if (value === null) return "n/a";
  return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(value)}%`;
}

/**
 * The change against the prior period, as a ratio difference rather than a
 * percentage of a ratio - a "+12%" on a number that is itself a rate is the
 * kind of figure nobody can say the meaning of out loud.
 *
 * An absent delta returns the empty string, and the caller renders nothing.
 * A "0.00" would claim the value held steady, which is a different fact from
 * having no prior period to compare against.
 */
export function formatDeltaRatio(value: number | null): string {
  if (value === null) return "";
  const sign = value > 0 ? "+" : "";
  const arrow = value >= 0 ? "↑" : "↓";
  return `${arrow} ${sign}${formatRatio(value)}`;
}

export const PROVISIONAL_TEXT = "Provisional - attribution still maturing";

export function maturityLabel(maturity: Maturity): { text: string; show: boolean } {
  if (maturity === "closed") return { text: "", show: false };
  return { text: PROVISIONAL_TEXT, show: true };
}

/** Days after month end before a month's attribution is final (the producer's maturity rule). */
const MATURITY_DAYS = 14;

/**
 * Whether a monthly point is still provisional AS OF `reference` (bug 3f30402f).
 *
 * The producer stamps `provisional` when it writes the artefact; a stale
 * artefact would keep saying so after the window has passed. So the flag only
 * counts while `reference` is still inside the month's maturity window.
 */
export function isProvisionalMonth(point: { m: string; provisional?: boolean }, reference?: Date): boolean {
  if (point.provisional !== true) return false;
  if (!reference) return true;
  const [year, month] = point.m.split("-").map(Number);
  if (!year || !month) return false;
  const maturesAt = Date.UTC(year, month, 0) + MATURITY_DAYS * 86_400_000;
  return reference.getTime() < maturesAt;
}

export function severityTone(severity: "critical" | "warning" | "info"): Tone {
  if (severity === "critical") return "critical";
  if (severity === "warning") return "watch";
  return "neutral";
}

/**
 * The series for a grain, with zero-filled periods dropped.
 *
 * `present: false` means the source query returned no row and the producer wrote
 * a 0. Plotting those alongside real measurements draws a cliff to zero that
 * never happened - the single most misreadable thing this chart could do - so
 * they are removed rather than rendered as data.
 *
 * A grain the producer has not backfilled yet returns an empty array, and the
 * chart says it has nothing for that grain instead of silently falling back to
 * another one. A silent fallback would label a monthly line as daily.
 */
export function seriesFor(
  tile: {
    series: SeriesPoint[];
    series_daily: SeriesPoint[];
    series_weekly: SeriesPoint[];
    series_monthly: SeriesPoint[];
  },
  grain: Grain,
): SeriesPoint[] {
  const raw =
    grain === "daily"
      ? tile.series_daily
      : grain === "weekly"
        ? tile.series_weekly
        : // `series_monthly` mirrors `series`; fall back only within the SAME grain.
          tile.series_monthly?.length
          ? tile.series_monthly
          : tile.series;
  return (raw ?? []).filter((point) => point.present);
}
